import React, { useEffect, useState } from "react";
import DynamicTable from "../TabComponents/DynamicTable";
import { useDispatch, useSelector } from "react-redux";
import { fetchDynamicData, registerUser } from "../../../../../utils/Apis";
import { getValueByPath } from "../../../../../utils/getValues";
import {
  selectTabData,
  selectTabLoading,
} from "../../../../../features/dynamicSlice";
import AddIcon from "@mui/icons-material/Add";
import { Button, Chip } from "@mui/material";
import AddStudentModal from "../TabComponents/AddStudentModal";
import { jwtDecode } from "jwt-decode";

const columns = [
  { label: "User ID", key: "UserId", sortable: true },
  { label: "First Name", key: "firstName", sortable: true },
  { label: "Last Name", key: "lastName", sortable: true },
  {
    label: "Phone",
    key: "mobileNo",
    sortable: true,
  },
  {
    label: "Branch",
    key: "branchId.name",
    render: (row) => row.branchId?.name || "N/A",
    sortable: true,
  },
  {
    label: "Course",
    key: "studentDetails.courses[0]?.course._id",
    render: (row) => (
      <div style={{ display: "flex", overflowX: "auto", padding: "10px 0" }}>
        {row.studentDetails.courses?.map((course) => (
          <Chip
            key={course?.course?.courseId}
            label={course?.course?.name || "N/A"}
            style={{ marginRight: "10px" }}
          />
        ))}
      </div>
    ),
    sortable: true,
  },
  { label: "Fee Due", key: "studentDetails.feeDue", sortable: true },
  {
    label: "Status",
    key: "studentDetails.status",
    sortable: true,
  },
];

const StudentsTab = () => {
  const dispatch = useDispatch();
  const students = useSelector((state) => selectTabData(state, "students"));
  const loading = useSelector((state) => selectTabLoading(state, "students"));
  const token = useSelector((state) => state.auth.token);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({});

  const [openModal, setOpenModal] = useState(false);

  const handleAddStudent = () => {
    setOpenModal(true);
  };

  const { id } = jwtDecode(token); // decoded payload (like userId, role, exp, etc.)

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmitStudent = (data) => {
    // submit logic here
    dispatch(registerUser(data));
    setOpenModal(false);
  };

  const fetchUsers = async () => {
    try {
      const resultAction = await dispatch(
        fetchDynamicData({
          url: "/user/profiles",
          method: "POST",
          tab: "students",
          body: {
            filters: {
              role: "student",
            },
          },
          params: {},
        })
      );
    } catch (error) {}
  };
  // Fetch data once
  useEffect(() => {
    if (!students || students.length === 0) fetchUsers();
  }, []);

  // Filtered, searched and sorted data
  const processedData = students
    // Filter
    .filter((student) => {
      const status = student.userStatus?.toLowerCase();
      return statusFilter ? status === statusFilter.toLowerCase() : true;
    })
    // Search
    .filter((student) => {
      const fullText =
        `${student.firstName} ${student.lastName} ${student.UserId} ${student.branchId?.name} ${student.courses?.[0]?.name}`.toLowerCase();

      return fullText.includes(search.toLowerCase());
    })
    // Sort
    .sort((a, b) => {
      if (!sortField) return 0;

      const valA = getValueByPath(a, sortField);
      const valB = getValueByPath(b, sortField);

      if (valA === undefined || valB === undefined) return 0;

      return sortOrder === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
          ? 1
          : -1;
    });

  // Paginate
  const total = processedData.length;
  const start = (page - 1) * perPage;
  const paginatedData = processedData.slice(start, start + perPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="p-4 space-y-4">
      <AddStudentModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitStudent}
        title="Add New Student"
        // fields={fields}
        formData={formData}
        setFormData={setFormData}
        adminId={id}
      />
      <div className="p-4 my-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">Students List</h1>
        <Button
          variant="contained"
          onClick={handleAddStudent}
          endIcon={<AddIcon />}
        >
          Add Student
        </Button>
      </div>

      {/* Filter + Search */}
      <div className="flex gap-4 flex-wrap">
        <input
          type="text"
          placeholder="Search..."
          className="border rounded px-2 py-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="border rounded px-2 py-1"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="dropped">Dropped</option>
        </select>
      </div>

      <DynamicTable
        columns={columns}
        rows={paginatedData}
        total={total}
        page={page}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        onSort={handleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={loading}
      />
    </div>
  );
};

export default StudentsTab;
